/**
 * Customer testimonials — single source of truth for auth / verify proof.
 * Seeded from humblytics.com (humblytics-lp testimonials-grid.tsx).
 */

import janiProfile from "@/assets/profiles/jani-testimonial.png"
import domProfile from "@/assets/profiles/dom-trovato.jpg"
import norbiProfile from "@/assets/profiles/norbio-profile.avif"
import adrianProfile from "@/assets/profiles/adrian-palco-labs.jpg"
import andrewProfile from "@/assets/profiles/andrew-avatar.png"
import diegoProfile from "@/assets/profiles/diogo-profile.avif"
import jessyProfile from "@/assets/profiles/jessy-testimonial.jpeg"
import louisProfile from "@/assets/profiles/louiso-profile.avif"
import nicholasProfile from "@/assets/profiles/nicholas-profile.jpg"
import shaneProfile from "@/assets/profiles/shane-profile.jpg"

export type Testimonial = {
  id: string
  name: string
  role: string
  avatar: string
  quote: string
  stat: string
  statLabel: string
}

/** Three cards shown on /auth. Quotes trimmed to one line for the fold. */
export const AUTH_TESTIMONIALS: Testimonial[] = [
  {
    id: "jani",
    name: "Jani T",
    role: "Co-Founder, MatchDay Health",
    avatar: janiProfile,
    // Trimmed to two lines so all three cards share a quote height; the "7×"
    // stat already carries the outcome the full sentence ends on.
    quote: "Heatmaps showed 70% of users never scrolled to our booking button.",
    stat: "7×",
    statLabel: "Revenue in 90 days",
  },
  {
    id: "dom",
    name: "Dom Trovato",
    role: "Founder, The Host Report",
    avatar: domProfile,
    quote: "A benefit-oriented copy test drove a 63% higher conversion rate.",
    stat: "+63%",
    statLabel: "Conversion lift",
  },
  {
    id: "norbi",
    name: "Norbi",
    role: "Founder, Social See",
    avatar: norbiProfile,
    quote:
      "Our landing page booking conversion went from 2% to 8% in three months.",
    stat: "2%→8%",
    statLabel: "Booking CVR",
  },
]

/** Alternates kept for reuse (unused on /auth today). */
const TESTIMONIAL_ALTERNATES: Testimonial[] = [
  {
    id: "adrian",
    name: "Adrian",
    role: "Founder, Palco Labs",
    avatar: adrianProfile,
    quote:
      "A/B testing runs our entire high-ticket funnel. The attribution shows exactly which variant moves real revenue.",
    stat: "$1M+/mo",
    statLabel: "Revenue driven",
  },
  {
    id: "andrew",
    name: "Andrew",
    role: "Co-Founder, Dedication Agents",
    avatar: andrewProfile,
    quote:
      "A/B testing across every client site lifted conversion rates by 28%, rolled out in a week with no per-site dev work.",
    stat: "+28%",
    statLabel: "Conversion lift",
  },
  {
    id: "diego",
    name: "Diego",
    role: "Founder, Reflow",
    avatar: diegoProfile,
    quote:
      "Humblytics is now our preferred analytics tool for our agency and clients. Unmatched in speed, robustness, and user experience.",
    stat: "Agency",
    statLabel: "Preferred tool",
  },
  {
    id: "nicholas",
    name: "Nicholas",
    role: "Founder, 253 Media",
    avatar: nicholasProfile,
    quote:
      "It has quickly become a standard app in our tech stack for all clients. The data is accurate and easy to read.",
    stat: "1 yr",
    statLabel: "Agency standard",
  },
  {
    id: "jessy",
    name: "Jessy",
    role: "Founder, Prospactive",
    avatar: jessyProfile,
    quote:
      "An intuitive dashboard that makes data easy to understand, and A/B testing that keeps improving our clients' conversion rates.",
    stat: "Agency",
    statLabel: "Client reporting",
  },
  {
    id: "shane",
    name: "Shane",
    role: "Founder, Waking Digital",
    avatar: shaneProfile,
    quote:
      "Simple, straightforward analytics that clients love. Click and form tracking work out of the box.",
    stat: "Zero",
    statLabel: "Setup overhead",
  },
  {
    id: "louis",
    name: "Louis",
    role: "CEO & Co-Founder",
    avatar: louisProfile,
    quote:
      "Incredibly easy to set up, with a clean UI that makes managing analytics effortless. Simple, effective insights without the clutter.",
    stat: "5★",
    statLabel: "Review",
  },
]

/**
 * Proof shown on the trial gate (/onboarding/plan). Deliberately the
 * outcome-shaped quotes rather than the ease-of-use ones — at a payment step
 * the open question is "is this worth the money", not "is it easy to install".
 */
export const PLAN_TESTIMONIALS: Testimonial[] = [
  AUTH_TESTIMONIALS[0], // Jani — 7x revenue
  TESTIMONIAL_ALTERNATES[1], // Andrew — +28% across 30+ client sites
  AUTH_TESTIMONIALS[2], // Norbi — 2% to 8% booking CVR
]

/** Spotlight used by MobileTrustStrip on /verify. */
export const SPOTLIGHT_TESTIMONIAL = AUTH_TESTIMONIALS[2]
